import { Injectable, inject } from "@angular/core";
import { StateEmitter } from "../../../../common/libraries/emitter/state-emitter";

import { SignUpEntryService } from "./entry.service";
import { OAuth2SignupAccount } from "../../../../common/services/api/resources/oauth2SignupAccount/oauth-2-signup-account";
import { OAuth2SignupAccountService } from "@onsip/common/services/api/resources/oauth2SignupAccount/oauth-2-signup-account.service";
import { SignupValidateDomainService } from "@onsip/common/services/api/resources/SignupValidateDomainService/signupValidateDomain.service";
import { SignupFreemiumAccountService } from "@onsip/common/services/api/resources/signupFreemiumAccount/signup-freemium-account.service";
import { Account } from "@onsip/common/services/api/resources/account/account";

const debug = true;

interface AccountInfo {
  domain: string;
  name: string;
  username: string;
  password: string;
  orgName: string;
}

interface SignupFreemiumAccountResponse extends Account {
  domain: string;
  username: string;
}

@Injectable({ providedIn: "root" })
export class SignUpCreateAccountService extends StateEmitter<AccountInfo> {
  private signupValidateDomainService = inject(SignupValidateDomainService);
  private entry = inject(SignUpEntryService);
  private oauth2SignupAccountService = inject(OAuth2SignupAccountService);
  private signupFreemiumAccountService = inject(SignupFreemiumAccountService);

  constructor() {
    super({
      domain: "",
      name: "",
      username: "",
      password: "",
      orgName: ""
    });
    this.init();
  }

  init(): void {
    const entryInfo: AccountInfo = {
      domain: this.entry.domain || "",
      name: this.entry.name || "",
      username: this.entry.username || "",
      password: "",
      orgName: this.entry.orgName || ""
    };
    this.stateStore = entryInfo;
    this.publishState();
  }

  get domain(): string {
    return this.stateValue.domain || "";
  }

  set domain(domain: string) {
    debug && console.log("set domain", domain);
    this.stateStore.domain = domain;
    this.publishState();
  }

  get name(): string {
    return this.stateValue.name || "";
  }

  set name(name: string) {
    debug && console.log("set name", name);
    this.stateStore.name = name;
    this.publishState();
  }

  get username(): string {
    return this.stateValue.username;
  }

  set username(username: string) {
    debug && console.log("set username", username);
    this.stateStore.username = username;
    this.publishState();
  }

  get orgName(): string {
    return this.stateValue.orgName;
  }

  set orgName(orgName: string) {
    debug && console.log("set org", this.orgName);
    this.stateStore.orgName = orgName;
    this.publishState();
  }

  get password(): string {
    return this.stateValue.password;
  }

  set password(password: string) {
    debug && console.log("set password", password);
    this.stateStore.password = password;
    this.publishState();
  }

  checkDomain(domain: string): Promise<boolean> {
    debug && console.log("check domain", domain);
    // Return Promise<boolean> where no error -> true, error -> false
    return this.signupValidateDomainService.signupValidateDomain(domain + ".onsip.com").then(
      () => true,
      err => {
        console.error("Error while checking domain", err.message);
        return false;
      }
    );
  }

  createFreemiumAccount(
    email: string | undefined,
    token: string | undefined,
    orgName: string | undefined,
    phoneNumber: string | undefined,
    plan: string | undefined
  ): Promise<SignupFreemiumAccountResponse> {
    return this.signupFreemiumAccountService
      .verifiedSignupFreemiumAccount({
        VerificationToken: token,
        ContactName: this.name,
        ContactOrganization: orgName || this.orgName,
        DomainSubdomain: this.domain,
        UserPassword: this.password,
        UserAddressUsername: this.username,
        ContactPhone: phoneNumber,
        AccountPlan: plan,
        ContactEmail: email
      })
      .then(account => ({
        ...account,
        username: this.username,
        domain: `${this.domain}.onsip.com`
      }));
  }

  createOauthAccount(
    email: string | undefined,
    oAuthService: string | undefined,
    oAuthServiceUserId: string | undefined,
    oauthToken: string | undefined,
    orgName: string | undefined,
    phoneNumber: string | undefined,
    plan: string | undefined
  ): Promise<OAuth2SignupAccount> {
    return this.oauth2SignupAccountService.oAuth2SignupAccount({
      OAuthService: oAuthService,
      OAuthServiceUserId: oAuthServiceUserId,
      OAuthToken: oauthToken,
      ContactName: this.name,
      ContactOrganization: orgName,
      DomainSubdomain: this.domain,
      UserAddressUsername: this.username,
      ContactPhone: phoneNumber,
      AccountPlan: plan,
      ContactEmail: email
    });
  }
}
